import { useEffect, useState } from 'react';
import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  Typography,
  useTheme,
} from '@mui/material';
import { get, useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';

import { getReviewsFields, Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import { FormProvider, RHFAutocomplete, RHFTextField, Scrollbar } from '@app/components';
import { IReview } from '../types';
import { useRequestState } from '@app/hooks';
import { StyledSlider, StyledSliderOverall } from '../styles';
import { reviewMarks } from './review-marks';
import { addReviewSchema } from '../validator';
import { selectAddReviewState, selectSearchUserState, selectUpdateReviewState } from '../selecter';
import { addReview, getReviews, searchUser, updateReview } from '../slice';
import { useAuthRole } from '@app/utils';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: IReview;
  isTLSelected?: boolean;
}
type RatingLabels = Record<number, string>;
type Ratings = Record<string, number>;
const ratingLabels: RatingLabels = {
  20: 'Poor',
  40: 'Average',
  60: 'Good',
  80: 'VeryGood',
  100: 'Excellent',
};
type RatingValue = keyof typeof ratingLabels;

const AddReview = ({ open, onClose, row, isTLSelected }: Props) => {
  const dispatch = useDispatch();
  const { isManager, isSuperAdmin } = useAuthRole();
  const theme = useTheme();

  const isManagerSelected = isManager || !isTLSelected;

  const getNumericValue = (label: string): number => {
    const numericValue = Object.keys(ratingLabels).find((key: any) => ratingLabels[key] === label);
    return numericValue ? Number(numericValue) : 0;
  };

  const { data } = useRequestState({
    stateSelector: selectSearchUserState,
  });

  const methods = useForm<IReview>({
    resolver: yupResolver(addReviewSchema) as any,
    defaultValues: {
      reviewTo: row
        ? {
            id: row?.usersReviewTo?.id,
            label: `${row?.usersReviewTo?.firstName ?? ''} ${row?.usersReviewTo?.lastName ?? ''}`,
          }
        : '',
      behavior: get(row, 'behavior', 'Poor'),
      taskPerformance: get(row, 'taskPerformance', 'Poor'),
      projectInvolvement: get(row, 'projectInvolvement', 'Poor'),
      communicationSkills: get(row, 'communicationSkills', 'Poor'),
      learningSkills: get(row, 'learningSkills', 'Poor'),
      decisionMakingSkills: get(row, 'decisionMakingSkills', 'Poor'),
      leadershipSkills: get(row, 'leadershipSkills', 'Poor'),
      fairTreatment: get(row, 'fairTreatment', 'Poor'),
      description: get(row, 'description', ''),
      disciplineManners: get(row, 'disciplineManners', 'Poor'),
    },
  });
  const getFields = () => {
    if (isManagerSelected) {
      return getReviewsFields.manager;
    } else {
      return getReviewsFields.teamMember;
    }
  };
  const [ratings, setRatings] = useState<Ratings>({});
  const [overallRating, setOverallRating] = useState<number>(0);
  const calculateOverallRating = (updatedRatings: Ratings) => {
    const values = Object.values(updatedRatings);
    const total = values.reduce((acc, current) => acc + current, 0);
    const average = total / reviews.length;

    let nearestRating = 0;
    let minDifference = Infinity;
    for (const ratingValue in ratingLabels) {
      const difference = Math.abs(parseFloat(ratingValue) - average);
      if (difference < minDifference) {
        minDifference = difference;
        nearestRating = parseFloat(ratingValue);
      }
    }
    setOverallRating(nearestRating);
  };
  const handleChange = (fieldName: any, value: RatingValue) => {
    const rating = ratingLabels[value];

    setRatings(() => {
      const newRatings = { ...ratings, [fieldName]: value };
      calculateOverallRating(newRatings);
      return newRatings;
    });
    methods.setValue(fieldName, rating, { shouldValidate: true, shouldDirty: true });
  };

  useEffect(() => {
    if (open || row) {
      const initialRatings = {
        behavior: getNumericValue(row?.behavior ?? '0'),
        taskPerformance: getNumericValue(row?.taskPerformance ?? '0'),
        projectInvolvement: getNumericValue(row?.projectInvolvement ?? '0'),
        communicationSkills: getNumericValue(row?.communicationSkills ?? '0'),
        learningSkills: getNumericValue(row?.learningSkills ?? '0'),
        decisionMakingSkills: getNumericValue(row?.decisionMakingSkills ?? '0'),
        leadershipSkills: getNumericValue(row?.leadershipSkills ?? '0'),
        fairTreatment: getNumericValue(row?.fairTreatment ?? '0'),
        disciplineManners: getNumericValue(row?.disciplineManners ?? '0'),
      };
      setRatings(initialRatings);
      calculateOverallRating(initialRatings);
      if (row?.overall) {
        setOverallRating(getNumericValue(row.overall));
      } else {
        const initialOverallRating = 20;
        setOverallRating(initialOverallRating);
      }
    }
  }, [open, row]);
  useEffect(() => {
    dispatch(searchUser());
    dispatch(getReviews());
  }, [dispatch]);

  const { loading } = useRequestState({
    stateSelector: row ? selectUpdateReviewState : selectAddReviewState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });
  const overall = ratingLabels[overallRating];
  const onSubmit = (form: IReview) => {
    const payload = { ...form, overall };
    row
      ? dispatch(
          updateReview({ ...payload, id: row.id, isManagerSelected, reviewTo: form?.reviewTo?.id })
        )
      : dispatch(addReview({ ...payload, isManagerSelected, reviewTo: form?.reviewTo?.id }));
  };
  const [query, setQuery] = useState();

  useEffect(() => {
    dispatch(searchUser(query));
  }, [dispatch, query]);

  const reviews = getFields();

  return (
    <>
      <FormProvider methods={methods}>
        <Dialog open={open} fullWidth>
          <Header
            variant="dialog"
            title={row ? Strings.common.update : Strings.field.addNew}
            onClose={onClose}
          />
          <Divider />
          <Scrollbar>
            <DialogContent>
              <Stack flex={1} my={2} gap={1}>
                <RHFAutocomplete
                  name="reviewTo"
                  label={Strings.field.reviewTo}
                  required
                  options={
                    isSuperAdmin
                      ? data
                        ? (isTLSelected
                            ? data.filter((option: any) => option.roleId === 2)
                            : data.filter((option: any) => option.roleId !== 2)
                          ).map((option: any) => ({
                            label: option.firstName,
                            id: option.id,
                          }))
                        : []
                      : data?.map((option: any) => ({
                          label: option.firstName,
                          id: option.id,
                        })) ?? []
                  }
                  getOptionLabel={(option: any) => option.label || ''}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                  onInputChange={(event, newInputValue: any) => {
                    setQuery(newInputValue);
                  }}
                />
              </Stack>
              {reviews.map((field) => (
                <Stack
                  key={field.fieldName}
                  my={2}
                  mt={1}
                  sx={{ display: 'flex', alignSelf: 'center' }}
                >
                  <Typography sx={{ color: theme.palette.grey[600] }}>{field.label}</Typography>
                  <StyledSlider
                    aria-label="Restricted values"
                    step={null}
                    defaultValue={
                      row ? getNumericValue(row[field.fieldName as keyof IReview] ?? '') : 20
                    }
                    valueLabelDisplay="auto"
                    marks={reviewMarks}
                    sx={{ height: 12, width: '90%', alignSelf: 'center' }}
                    onChange={(event, value) => {
                      handleChange(field.fieldName, value as RatingValue);
                    }}
                  />
                </Stack>
              ))}
              <Stack my={2} mt={1} sx={{ display: 'flex', alignSelf: 'center' }}>
                <Typography sx={{ color: theme.palette.grey[600] }}>
                  {Strings.reviews.overallRating}
                </Typography>
                <StyledSliderOverall
                  aria-label="Restricted values"
                  step={null}
                  valueLabelDisplay="auto"
                  marks={reviewMarks}
                  sx={{ height: 12, width: '90%', alignSelf: 'center' }}
                  disabled
                  value={overallRating}
                />
              </Stack>
              <Stack my={2} mt={1} sx={{ display: 'flex', alignSelf: 'center' }}>
                <RHFTextField
                  name="description"
                  label={Strings.field.description}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={5}
                />
              </Stack>
            </DialogContent>
          </Scrollbar>

          <Divider sx={{ pt: 2 }} />
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              {Strings.button.cancel}
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={loading}
              onClick={methods.handleSubmit(onSubmit)}
            >
              {Strings.button.save}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </FormProvider>
    </>
  );
};

export { AddReview };
